import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

export default function Login() {
  const { user, ready, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { if (ready && user) navigate("/admin", { replace: true }); }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(apiErr(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950 grain flex items-center justify-center px-5 relative">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <span className="w-3 h-3 rounded-full bg-brand green-glow" />
          <span className="font-display font-extrabold text-xl text-white">Lucrative<span className="text-brand">Laptop</span></span>
        </div>
        <div className="rounded-2xl border border-ink-600 bg-ink-900 p-8 green-glow">
          <div className="label-mono mb-2">Admin Access</div>
          <h1 className="font-display font-extrabold text-white text-2xl mb-6">Sign in to the studio</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 font-mono-label">Email</label>
              <div className="mt-1.5 flex items-center gap-2 bg-ink-800 border border-ink-600 rounded-lg px-3.5 py-3 focus-within:border-brand transition-colors">
                <Mail size={16} className="text-slate-500" />
                <input type="email" required data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@lucrativelaptop.com" className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-600" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-mono-label">Password</label>
              <div className="mt-1.5 flex items-center gap-2 bg-ink-800 border border-ink-600 rounded-lg px-3.5 py-3 focus-within:border-brand transition-colors">
                <Lock size={16} className="text-slate-500" />
                <input type="password" required data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-600" />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand text-brand-dark font-bold py-3 rounded-lg hover:bg-brand-neon transition-colors disabled:opacity-60 mt-2">
              {loading ? "Signing in..." : "Sign in"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
